import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Logo } from '#app/components/common/Logo'
import { applyCollectionEntityToPath } from '#app/utils/pz/brand/collectionEntity'
import {
	buildBrandInterpolationVariables,
	buildRuntimeBrandInterpolationConfig,
	type RuntimeBrandEnv,
} from '#app/utils/pz/brand/runtimeBrandConfig'
import { useBrandData } from '#app/utils/pz/brand/useBrandData'
import { useFeatureFlags } from '#app/utils/pz/featureFlags'
import { buildCategoryTree } from '#app/utils/pz/navigation/categoryTree'
import { buildMarketingMenus } from '#app/utils/pz/navigation/menuBuilders'
import { build, path, publicRoutes } from '#app/utils/pz/routes'
import { useOptionalUser } from '#app/utils/user'
import { Icon, type IconName } from '../ui/icon'

type FooterCategory = {
	id: string
	name: string
	slug: string
	parentId?: string | null
	position?: number | null
}

type FooterCollection = {
	id: string
	title: string
	slug: string
	accessLevel?: string | null
}

type FooterProps = {
	navRoutes?: any[]
	navPaths?: any
	categories?: FooterCategory[]
	collections?: FooterCollection[]
	signupHrefOverride?: string
}

function resolveTwitterUrl(
	brandTwitterUrl?: string | null,
	brandDomain?: string | null,
) {
	const trimmedTwitter = brandTwitterUrl?.trim()
	if (trimmedTwitter) {
		if (
			trimmedTwitter.startsWith('http://') ||
			trimmedTwitter.startsWith('https://')
		) {
			return trimmedTwitter
		}
		if (trimmedTwitter.startsWith('@')) {
			return `https://x.com/${trimmedTwitter.slice(1)}`
		}
		if (!trimmedTwitter.includes('/')) {
			return `https://x.com/${trimmedTwitter}`
		}
		return `https://${trimmedTwitter}`
	}

	const normalizedDomain = brandDomain?.trim()
	if (!normalizedDomain) return undefined

	try {
		const domainWithScheme = normalizedDomain.startsWith('http')
			? normalizedDomain
			: `https://${normalizedDomain}`
		const hostname = new URL(domainWithScheme).hostname.replace(/^www\./, '')
		if (!hostname) return undefined
		return `https://x.com/${hostname}`
	} catch {
		return undefined
	}
}

// Footer component
export const SMkFooterMenu = ({
	navRoutes,
	navPaths,
	categories,
	collections,
	signupHrefOverride,
}: FooterProps) => {
	const { t, i18n } = useTranslation()
	const translate = useCallback(
		(key: string) => {
			const result = t(key as any)
			return typeof result === 'string' ? result : String(result)
		},
		[t],
	)
	const featureFlags = useFeatureFlags()
	const user = useOptionalUser()
	const ctx = useMemo(() => ({ featureFlags, user }), [featureFlags, user])
	const rootData = useBrandData()
	const rootEnv = (rootData as { ENV?: RuntimeBrandEnv } | undefined)?.ENV
	const footerBrandVariables = useMemo(
		() =>
			buildBrandInterpolationVariables(
				buildRuntimeBrandInterpolationConfig(rootEnv, rootEnv?.BRAND),
				i18n.language?.startsWith('en') ? 'en' : 'es',
			),
		[rootEnv, i18n.language],
	)
	const catalogPath = publicRoutes.courses.list()
	const catalogDiscoveryPath = publicRoutes.catalog.index()
	const programsListPath = publicRoutes.programs.list()
	const legalBasePath = publicRoutes.legal()
	const signupPath = path('auth.signup')

	const { tools, social } = useMemo(() => {
		return buildMarketingMenus({
			t: translate,
			ctx,
			navRoutes,
			navPaths,
			signupHrefOverride,
		})
	}, [ctx, navPaths, navRoutes, signupHrefOverride, translate])

	const genericItems = useMemo(() => {
		const base = [...tools]
		if (!base.some((item) => item.href.startsWith(signupPath))) {
			base.push({
				id: -1000,
				name: t('marketing.nav.getStarted'),
				href: signupPath,
				icon: 'star',
			})
		}

		// Add pricing link to footer
		base.push({
			id: -1001,
			name: t('marketing.nav.pricing'),
			href: '/pricing',
			icon: 'card-stack',
		})

		base.push({
			id: -1002,
			name: t('marketing.nav.help'),
			href: publicRoutes.docs(),
			icon: 'question-mark-circled',
		})

		const seen = new Set<string>()
		return base.filter((item) => {
			if (!item.href) return false
			if (seen.has(item.href)) return false
			seen.add(item.href)
			return true
		})
	}, [signupPath, t, tools])

	const rootCategories = useMemo(() => {
		return buildCategoryTree(categories)
			.rootCategories.filter((item) => item.slug)
			.slice(0, 10)
	}, [categories])

	const collectionItems = useMemo(() => {
		const all = Array.isArray(collections) ? collections : []
		return all.filter((item) => item.slug && item.title).slice(0, 10)
	}, [collections])

	const legalLinks = [
		{
			name: t('marketing.legal.links.terms.title'),
			href: `${legalBasePath}/terms`,
		},
		{
			name: t('marketing.legal.links.privacy.title'),
			href: `${legalBasePath}/privacy`,
		},
		{
			name: t('marketing.legal.links.cookies.title'),
			href: `${legalBasePath}/cookies`,
		},
	]

	const brandContactEmail = rootData?.brandContactEmail
	const brandTwitterUrl = rootData?.brandTwitterUrl
	const brandDomain = rootData?.brandDomain
	const brandName = rootData?.brandShortName ?? rootData?.brandTitle
	const socialConfig = social
	const resolvedTwitterUrl = resolveTwitterUrl(brandTwitterUrl, brandDomain)

	const socialFallback: Array<{
		name: string
		href: string
		icon: IconName
		target: string | undefined
	}> = [
		{
			name: t('marketing.social.twitter'),
			href: resolvedTwitterUrl ?? '#',
			icon: 'twitter-logo' as IconName,
			target: resolvedTwitterUrl ? '_blank' : undefined,
		},
		{
			name: t('marketing.social.email'),
			href: brandContactEmail ? `mailto:${brandContactEmail}` : '#',
			icon: 'envelope-closed' as IconName,
			target: undefined,
		},
	].filter((item) => item.href !== '#')

	const socialLinks = useMemo(() => {
		const base = (socialConfig ?? []).map((item) => ({
			name: item.name,
			href: item.href,
			icon: item.icon as IconName,
			target: item.target,
		}))

		const hasTwitter = base.some((item) => item.icon === 'twitter-logo')
		const hasEmail = base.some((item) => item.href.startsWith('mailto:'))
		if (!hasTwitter) {
			const twitterFallback = socialFallback.find(
				(item) => item.icon === 'twitter-logo',
			)
			if (twitterFallback) base.push(twitterFallback)
		}
		if (!hasEmail) {
			const emailFallback = socialFallback.find(
				(item) => item.icon === 'envelope-closed',
			)
			if (emailFallback) base.push(emailFallback)
		}

		const seen = new Set<string>()
		return base.filter((item) => {
			const key = `${item.icon}:${item.href}`
			if (seen.has(key)) return false
			seen.add(key)
			return true
		})
	}, [socialConfig, socialFallback])

	return (
		<footer className="bg-muted border-t" aria-labelledby="footer-heading">
			<div className="mx-auto max-w-screen-xl px-4 py-10 sm:px-6 lg:px-8 lg:py-12">
				<h2 id="footer-heading" className="sr-only">
					{t('marketing.footer.title')}
				</h2>

				<div className="mb-8 flex flex-col justify-between gap-5 sm:flex-row sm:items-center">
					<Link to={publicRoutes.home()}>
						<Logo />
					</Link>
					<div className="flex flex-wrap items-center gap-2">
						{socialLinks.map((item) => (
							<a
								key={`${item.name}-${item.href}`}
								href={item.href}
								className="border-border bg-background text-muted-foreground hover:text-foreground hover:border-foreground/30 inline-flex h-9 w-9 items-center justify-center rounded-full border transition"
								target={item.target}
								rel={item.target === '_blank' ? 'noreferrer' : undefined}
							>
								<span className="sr-only">{item.name}</span>
								<Icon name={item.icon} className="h-4 w-4" aria-hidden="true" />
							</a>
						))}
					</div>
				</div>

				<div className="grid gap-8 sm:grid-cols-2 xl:grid-cols-3">
					<div>
						<h3 className="text-label text-foreground">
							{t('navigation.sidebar.collections')}
						</h3>
						<ul className="mt-4 space-y-2.5">
							{collectionItems.length > 0 ? (
								collectionItems.map((collection) => (
									<li key={`collection-${collection.id}`}>
										<Link
											to={applyCollectionEntityToPath(
												build('marketing.collectionDetail', {
													collectionSlug: collection.slug,
												}),
											)}
											className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2 text-sm transition"
										>
											<span>{collection.title}</span>
											{collection.accessLevel === 'free' ? (
												<span className="text-label bg-primary/15 text-primary rounded px-1.5 py-0.5">
													{t('marketing.footer.freeBadge')}
												</span>
											) : null}
										</Link>
									</li>
								))
							) : (
								<li className="text-muted-foreground text-sm">
									{t('common.collections.noCollections')}
								</li>
							)}
							<li>
								<Link
									to={programsListPath}
									reloadDocument
									className="text-sm font-medium underline underline-offset-4"
								>
									{String(
										t(
											'marketing.footer.viewAllCollections' as any,
											footerBrandVariables,
										),
									)}
								</Link>
							</li>
						</ul>
					</div>

					<div>
						<h3 className="text-label text-foreground">
							{t('marketing.footer.sections.categoryRoutes')}
						</h3>
						<ul className="mt-4 space-y-2.5">
							{rootCategories.length > 0 ? (
								rootCategories.map((category) => (
									<li key={category.id}>
										<Link
											to={`${catalogPath}?category=${encodeURIComponent(category.slug)}`}
											reloadDocument
											className="text-muted-foreground hover:text-foreground text-sm transition"
										>
											{category.name}
										</Link>
									</li>
								))
							) : (
								<li className="text-muted-foreground text-sm">
									{t('marketing.footer.noCategories')}
								</li>
							)}
						</ul>
						<div className="mt-4">
							<Link
								to={catalogDiscoveryPath}
								reloadDocument
								className="text-sm font-medium underline underline-offset-4"
							>
								{t('marketing.nav.viewFullCatalog')}
							</Link>
						</div>
					</div>

					<div>
						<h3 className="text-label text-foreground">
							{t('marketing.footer.sections.more')}
						</h3>
						<ul className="mt-4 space-y-2.5">
							{genericItems.map((item) => (
								<li key={`generic-${item.id}`}>
									{item.target ||
									item.href.startsWith('http') ||
									item.href.startsWith('mailto:') ? (
										<a
											href={item.href}
											target={item.target}
											rel={item.target === '_blank' ? 'noreferrer' : undefined}
											className="text-muted-foreground hover:text-foreground text-sm transition"
										>
											{item.name}
										</a>
									) : (
										<Link
											to={item.href}
											className="text-muted-foreground hover:text-foreground text-sm transition"
										>
											{item.name}
										</Link>
									)}
								</li>
							))}
						</ul>
					</div>
				</div>

				<div className="border-border/70 mt-8 border-t pt-6">
					<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
						<p className="text-muted-foreground text-xs">
							{t('marketing.footer.copyright', {
								year: new Date().getFullYear(),
								brandName: brandName ?? '',
							})}
						</p>
						<div className="flex flex-wrap items-center gap-4">
							{legalLinks.map((item) => (
								<Link
									key={`bottom-${item.href}`}
									to={item.href}
									prefetch="intent"
									className="text-muted-foreground hover:text-foreground text-xs transition"
								>
									{item.name}
								</Link>
							))}
						</div>
					</div>
				</div>
			</div>
		</footer>
	)
}
